import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeSlash, GoogleLogo } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success(`Welcome back, ${r.user.full_name || "investor"}!`);
      const to = r.user.role === "admin" ? "/admin" : (loc.state?.from || "/dashboard");
      nav(to);
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f8fafc]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhZnJpY2FuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODU1Mzk4NDJ8MA&ixlib=rb-4.1.0&q=85" alt="Login visual" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1128]/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
            <span className="font-display font-black text-2xl">MAYOTO</span>
          </Link>
          <div>
            <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">INVESTOR LOGIN</p>
            <h1 className="mt-3 font-display font-black text-5xl leading-tight max-w-md">Welcome back to Mayoto.</h1>
            <p className="mt-4 text-slate-300 max-w-md">Access your portfolio, track your investments and explore new opportunities.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0a1128]"><span className="text-[#00b4d8] font-black text-xl font-display">M</span></span>
            <span className="font-display font-black text-xl text-[#0a1128]">MAYOTO</span>
          </Link>
          <h2 className="font-display font-black text-3xl text-[#0a1128]" data-testid="login-title">Sign in</h2>
          <p className="mt-2 text-slate-600">Access your investor account.</p>

          <Button
            type="button"
            onClick={startGoogleSignin}
            className="mt-6 w-full bg-white text-[#0a1128] border border-slate-300 hover:bg-slate-50 rounded-full py-6 text-base font-semibold flex items-center justify-center gap-3"
            data-testid="login-google"
          >
            <GoogleLogo size={20} weight="bold" className="text-[#00b4d8]" />
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-400 tracking-wider">OR SIGN IN WITH EMAIL</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="login-remember" /> Remember me
              </label>
              <button type="button" onClick={() => nav("/forgot-password")} className="text-sm text-[#00b4d8] hover:text-[#0096c7]" data-testid="login-forgot">Forgot password?</button>
            </div>
            <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-6 text-base" data-testid="login-submit">
              {loading ? "Signing in…" : "Login"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Don't have an account? <Link to="/register" className="text-[#00b4d8] font-semibold hover:underline" data-testid="login-goto-register">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
